/**
 * Public agent landing page — /agent/:slug.
 * Renders the agent's photo, bio, specialties, contact buttons and a
 * lead-capture form that posts to /api/public/agent/{slug}/lead.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Send, CheckCircle2, Phone, Mail, MessageSquare, ShieldCheck, MapPin, Award } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const PublicAgent = () => {
  const { slug } = useParams();
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [form, setForm] = useState({ name: '', phone: '', email: '', product: '', message: '' });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/agent/${slug}`)
      .then(r => setAgent(r.data))
      .catch(e => setErr(e.response?.data?.detail || 'Agent page not found'))
      .finally(() => setLoading(false));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    const phoneDigits = (form.phone || '').replace(/\D/g, '');
    if (!form.name.trim()) return alert('Please enter your name');
    if (phoneDigits.length !== 10) return alert('Please enter a valid 10-digit phone');
    setSending(true);
    try {
      await axios.post(`${API}/public/agent/${slug}/lead`, {
        name: form.name.trim(), phone: phoneDigits, email: form.email || null,
        product: form.product || null, message: form.message || null, consent: true,
      });
      setDone(true);
    } catch (ex) {
      alert(ex.response?.data?.detail || 'Submission failed');
    } finally { setSending(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="spinner" /></div>;
  if (err) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="text-center max-w-md" data-testid="agent-page-error">
        <p className="text-slate-700 text-lg">{err}</p>
      </div>
    </div>
  );

  const photoUrl = agent.photo ? (agent.photo.startsWith('http') ? agent.photo : `${BACKEND}${agent.photo}`) : null;
  const logoUrl = agent.logo_url ? (agent.logo_url.startsWith('http') ? agent.logo_url : `${BACKEND}${agent.logo_url}`) : null;
  const waLink = agent.phone ? `https://wa.me/91${agent.phone.replace(/\D/g, '').slice(-10)}` : null;
  const accent = agent.accent_color || '#059669';

  return (
    <div className="min-h-screen" data-testid="public-agent-page" style={{ background: `linear-gradient(to bottom, ${accent}14, #ffffff 40%, #f8fafc)` }}>
      <header className="max-w-3xl mx-auto pt-12 pb-8 px-4 text-center">
        {logoUrl && (
          <img src={logoUrl} alt="logo" className="h-10 mx-auto mb-4 object-contain" data-testid="agent-logo" />
        )}
        {photoUrl && (
          <img src={photoUrl} alt={agent.name} className="h-32 w-32 rounded-full object-cover mx-auto ring-4 ring-white shadow-md" data-testid="agent-photo" style={{ boxShadow: `0 0 0 4px ${accent}33` }} />
        )}
        <h1 className="mt-4 text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }} data-testid="agent-name">{agent.name}</h1>
        {agent.tagline && <p className="mt-1 text-lg font-medium" style={{ color: accent }}>{agent.tagline}</p>}
        <div className="mt-3 flex flex-wrap items-center justify-center gap-3 text-sm text-slate-600">
          {agent.years_experience ? <span className="inline-flex items-center gap-1"><Award className="h-3.5 w-3.5" /> {agent.years_experience}+ years experience</span> : null}
          {agent.location ? <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {agent.location}</span> : null}
          <span className="inline-flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" style={{ color: accent }} /> Verified InsureGrowth agent</span>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 pb-16 space-y-6">
        {(agent.bio || (agent.specialties && agent.specialties.length > 0)) && (
          <section className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6" data-testid="agent-bio">
            {agent.bio && <p className="text-slate-700 whitespace-pre-wrap">{agent.bio}</p>}
            {agent.specialties && agent.specialties.length > 0 && (
              <div className="mt-4">
                <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Specialties</p>
                <div className="flex flex-wrap gap-2" data-testid="agent-specialties">
                  {agent.specialties.map(s => (
                    <span key={s} className="px-3 py-1 rounded-full text-xs font-medium border" style={{ color: accent, background: `${accent}14`, borderColor: `${accent}55` }}>{s}</span>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        <section className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6" data-testid="agent-contact-buttons">
          <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-3">Reach out directly</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {agent.phone && (
              <a href={`tel:${agent.phone}`} className="flex items-center justify-center gap-2 rounded-lg text-white font-medium py-3 hover:opacity-90" style={{ background: accent }} data-testid="agent-call-button">
                <Phone className="h-4 w-4" /> Call
              </a>
            )}
            {waLink && (
              <a href={waLink} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 rounded-lg bg-green-600 text-white font-medium py-3 hover:bg-green-700" data-testid="agent-whatsapp-button">
                <MessageSquare className="h-4 w-4" /> WhatsApp
              </a>
            )}
            {agent.email && (
              <a href={`mailto:${agent.email}`} className="flex items-center justify-center gap-2 rounded-lg border border-slate-300 text-slate-800 font-medium py-3 hover:bg-slate-50" data-testid="agent-email-button">
                <Mail className="h-4 w-4" /> Email
              </a>
            )}
          </div>
        </section>

        <section className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6" data-testid="agent-lead-form">
          <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Or drop your details — I'll get back to you</h2>
          <p className="text-sm text-slate-500 mt-1">Fastest way to get a quote or discuss coverage.</p>
          {done ? (
            <div className="mt-6 flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="agent-lead-done">
              <CheckCircle2 className="h-8 w-8 text-emerald-600 shrink-0" />
              <div>
                <p className="font-semibold text-emerald-900">Thanks, {form.name}!</p>
                <p className="text-sm text-emerald-800">{agent.name} will reach out to you shortly.</p>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-4 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input required placeholder="Your name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="agent-lead-name" />
                <input required inputMode="numeric" maxLength={10} placeholder="10-digit mobile *" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })} className="px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="agent-lead-phone" />
                <input type="email" placeholder="Email (optional)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="agent-lead-email" />
                <select value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="agent-lead-product">
                  <option value="">Interested in… (optional)</option>
                  {['Term Life', 'Health', 'Motor', 'Home', 'Travel', 'Personal Accident', 'Investment', 'Other'].map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <textarea placeholder="Anything specific? (optional)" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="agent-lead-message" />
              <button type="submit" disabled={sending} className="w-full text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-60 hover:opacity-90" style={{ background: accent }} data-testid="agent-lead-submit">
                <Send className="h-4 w-4" /> {sending ? 'Sending…' : 'Send Enquiry'}
              </button>
              <p className="text-[11px] text-slate-400 text-center">By submitting you agree to be contacted by {agent.name} at InsureGrowth.</p>
            </form>
          )}
        </section>

        <p className="text-center text-[11px] text-slate-400">Powered by <span className="text-emerald-700 font-medium">InsureGrowth</span></p>
      </div>
    </div>
  );
};

export default PublicAgent;
